import { useEffect, useRef } from 'react';
import type { Month13, Day13 } from '@/lib/calendar13Types';
import { WEEKDAY_NAMES } from '@/lib/calendar13Types';
import { cn } from '@/lib/utils';
import { format, isValid } from 'date-fns';
import { EventBadge } from './EventBadge';
import { useEventsForRange, useEventPriorityBadge } from '@/hooks/useEventsForRange';

interface MonthGridProps {
  month: Month13;
  today: Day13;
  selectedDay: Day13 | null;
  onSelectDay: (day: Day13) => void;
  liveDay?: number; // Live day number from ring rotation
  widgetSize?: { width: number; height: number }; // Dashboard widget dimensions (grid cols/rows)
  dashboardId?: string; // For namespacing events to shared dashboards
}

export function MonthGrid({ month, today, selectedDay, onSelectDay, liveDay, widgetSize, dashboardId }: MonthGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  
  // Get the date range for the month and fetch events
  const monthStart = month.days[0]?.gregorianDate ?? new Date();
  const monthEnd = month.days[month.days.length - 1]?.gregorianDate ?? new Date();
  const eventsByDate = useEventsForRange(monthStart, monthEnd, dashboardId);

  // Grow day size when more vertical space is available (tablet/desktop)
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const update = () => {
      const height = node.clientHeight || 0;
      const daySize = Math.max(14, Math.min(30, height / 6 - 4));
      node.style.setProperty('--calendar-day-size', `${daySize}px`);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [month.days.length]);

  // When widget is 1x1 (minimum size), show only the day number of the selected day
  const isMinimalMode = widgetSize && widgetSize.width <= 1 && widgetSize.height <= 1;
  
  if (isMinimalMode && selectedDay) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-bold text-primary mb-2">{selectedDay.day}</div>
          <p className="text-xs text-muted-foreground">
            {selectedDay.isOutOfTime ? 'Day Out of Time' : `${month.name}`}
          </p>
        </div>
      </div>
    );
  }
  const isToday = (day: Day13) => 
    day.year === today.year && 
    day.month === today.month && 
    day.day === today.day;
  
  const isSelected = (day: Day13) =>
    selectedDay &&
    day.year === selectedDay.year &&
    day.month === selectedDay.month &&
    day.day === selectedDay.day;

  const isLive = (day: Day13) => liveDay !== undefined && day.day === liveDay;

  const getDayStatusLabel = (day: Day13) => {
    const statuses: string[] = [];

    if (day.isOutOfTime) {
      statuses.push('Day Out of Time');
    }

    if (isToday(day)) {
      statuses.push('Today');
    }

    if (isSelected(day)) {
      statuses.push('Selected day');
    }

    if (isLive(day)) {
      statuses.push('Live day indicator');
    }

    const baseLabel = isValid(day.gregorianDate)
      ? `Galactic Day ${day.day} • ${format(day.gregorianDate, 'MMMM d, yyyy')}`
      : `Galactic Day ${day.day}`;

    if (!statuses.length) {
      return `${baseLabel} • Select to view details`;
    }

    return `${baseLabel} • ${statuses.join(' • ')}`;
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-0.5 grid w-full grid-cols-7 gap-0.5">
        {WEEKDAY_NAMES.map((name) => (
          <div
            key={name}
            className="py-0 text-center text-[8px] font-medium uppercase tracking-[0.12em] text-white/32"
          >
            {name.slice(0, 2)}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        className="grid w-full flex-1 min-h-0 auto-rows-fr grid-cols-7 gap-0.5"
      >
        {month.days.map((day) => {
          const dateKey = day.gregorianDate ? day.gregorianDate.toISOString().split('T')[0] : '';
          const dayEvents = eventsByDate[dateKey] || [];
          const { color, count, statuses } = useEventPriorityBadge(dayEvents);

          return (
            <button
              key={day.day}
              onClick={() => onSelectDay(day)}
              title={getDayStatusLabel(day)}
              aria-label={getDayStatusLabel(day)}
              className={cn(
                'calendar-day relative aspect-square h-full w-full overflow-hidden rounded-[14px] border border-white/5 bg-white/[0.03] text-[clamp(0.7rem,2.1vw,0.94rem)] font-medium text-white/86 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                isToday(day) && 'today border-cyan-300/35 bg-cyan-300/8 text-white',
                isSelected(day) && 'border-[#f6f0c9] bg-[#f5efcf] text-slate-950 shadow-[0_8px_18px_rgba(0,0,0,0.18)]',
                isLive(day) && !isSelected(day) && 'ring-2 ring-fuchsia-300/60 ring-offset-1 ring-offset-transparent',
                !isToday(day) && !isSelected(day) && 'hover:border-white/20 hover:bg-white/[0.08]'
              )}
            >
              {isToday(day) && !isSelected(day) ? (
                <span className="absolute left-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-cyan-200/90" />
              ) : null}
              {isSelected(day) ? (
                <span className="absolute left-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-slate-950/70" />
              ) : null}
              <div className="relative flex h-full items-center justify-center p-1">
                <span
                  className="leading-none"
                  style={{
                    lineHeight: 1,
                    fontSize: isSelected(day)
                      ? 'calc(var(--calendar-day-size, 1rem) * 1.03)'
                      : 'var(--calendar-day-size, 1rem)',
                  }}
                >
                  {day.day}
                </span>
              </div>
              {dayEvents.length > 0 ? (
                <span
                  className={cn(
                    'absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full',
                    isSelected(day) ? 'bg-slate-950/55' : 'bg-cyan-200/75',
                  )}
                />
              ) : null}
              <div className="absolute inset-0">
                <EventBadge color={color} count={count} statuses={statuses} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
